
import React, { useState, useEffect } from "react";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { calculatePricingPlans } from "@/utils/pricing";
import { PricingTabs } from "../PricingTabs";
import { useNavigation } from "@/hooks/use-navigation";
import { Badge } from "@/components/ui/badge";

interface CoachDetailsProps {
  coach: any;
  onClose?: () => void;
  selectedPlan?: any;
  onPlanSelect?: (plan: any) => void;
}

export const CoachDetails = ({
  coach,
  onClose,
  selectedPlan,
  onPlanSelect,
}: CoachDetailsProps) => {
  const navigate = useNavigation();
  const [activeTab, setActiveTab] = useState("about");
  const [plans, setPlans] = useState<any[]>([]);
  const [localSelectedPlan, setLocalSelectedPlan] = useState(selectedPlan);

  useEffect(() => {
    if (coach && coach.monthly_price) {
      const calculatedPlans = calculatePricingPlans(
        coach.monthly_price,
        coach.per_month_extend_discount || 0.95
      );
      setPlans(calculatedPlans);

      if (!selectedPlan && calculatedPlans.length > 0) {
        setLocalSelectedPlan(calculatedPlans[0]);
      }
    }
  }, [coach, selectedPlan]);

  const handleProceedClick = () => {
    if (!localSelectedPlan) return;

    localStorage.setItem('selectedCoach', JSON.stringify({
      ...coach,
      selectedPlan: localSelectedPlan
    }));

    navigate('/user-details');
    if (onClose) onClose();
  };

  const handlePlanSelect = (plan: any) => {
    setLocalSelectedPlan(plan);
    if (onPlanSelect) {
      onPlanSelect(plan);
    }
  };

  const hasAvailableSlots = coach.remaining_slots > 0;

  return (
    <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-2xl font-bold flex items-center justify-between">
          {coach.name_surname}
          {coach.remaining_slots !== null && coach.remaining_slots <= 3 && (
            coach.remaining_slots === 0 ? (
              <Badge variant="destructive">Kontenjan doldu</Badge>
            ) : (
              <Badge variant="destructive">
                {coach.remaining_slots === 1
                  ? "Son kontenjan!"
                  : `Son ${coach.remaining_slots} kontenjan`}
              </Badge>
            )
          )}
        </DialogTitle>
      </DialogHeader>

      <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="mt-4">
        <TabsList className="grid grid-cols-2 mb-4">
          <TabsTrigger value="about">Hakkında</TabsTrigger>
          <TabsTrigger value="pricing">Mentorluk Planları</TabsTrigger>
        </TabsList>

        <TabsContent value="about" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <img
                src={"/coaches/" + coach.photo_url + ".jpg" || "/coaches/missing.jpg"}
                alt={coach.name_surname}
                className="w-full aspect-[3/4] object-cover rounded-lg"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/coaches/missing.jpg";
                }}
              />
            </div>
            <div className="md:col-span-2 space-y-4">
              <div>
                <h3 className="text-lg font-semibold">Akademik Bilgiler</h3>
                <p className="text-lg font-medium text-brand-primary">{coach.university}</p>
                <p className="text-brand-primary/70">{coach.major}</p>
              </div>

              {coach.placement_types && coach.placement_ranks && (
                <div>
                  <h3 className="text-lg font-semibold">Sınav Başarıları</h3>
                  <div className="space-y-2">
                    {coach.placement_types.map((type: string, index: number) => (
                      <div key={`${type}-${index}`} className="flex items-center space-x-2">
                        <span className="font-medium">{type}:</span>
                        <span className="text-brand-primary font-semibold">
                          {coach.placement_ranks[index] && parseInt(coach.placement_ranks[index]).toLocaleString('tr-TR')}. sıralama
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {coach.coach_description && (
                <div>
                  <h3 className="text-lg font-semibold">Koçluk Yaklaşımı</h3>
                  <p className="text-brand-primary/70 whitespace-pre-line">{coach.coach_description}</p>
                </div>
              )}
            </div>
          </div>

          {coach.personal_description && (
            <div>
              <h3 className="text-lg font-semibold">Kişisel Hikaye</h3>
              <p className="text-brand-primary/70 whitespace-pre-line">{coach.personal_description}</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="pricing">
          {plans.length > 0 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-brand-primary mb-2">Mentorluk Planları</h3>
                <p className="text-brand-primary/70">
                  Size en uygun olan planı seçin
                </p>
              </div>

              <PricingTabs
                plans={plans}
                selectedPlan={localSelectedPlan}
                onSelectPlan={handlePlanSelect}
                showActionButton={false}
              />

              {!hasAvailableSlots ? (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md text-center">
                  <p className="font-medium">Bu koç için kontenjan dolmuştur.</p>
                  <p className="text-sm mt-1">Lütfen başka bir koç seçiniz.</p>
                </div>
              ) : (
                <Button
                  className="w-full mt-8"
                  size="lg"
                  onClick={handleProceedClick}
                >
                  Bu Koç ile Devam Et
                </Button>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </DialogContent>
  );
};
